import React, { useEffect, useState } from "react";
import { X, Plus, Trash2, AlertTriangle } from "lucide-react";
import inventoryitemService from "../../../../services/inventoryitemService";
import { useAuth } from "@/context/AuthContext";
import { P } from "@/config/permissionCodes";
import toast from "react-hot-toast";

export default function InventoryCategoryModal({
  categories,
  setCategories,
  onClose,
  onSave,
}) {
  const { hasPermission } = useAuth();

  const canAddCategory      = hasPermission(P.INVENTORY.ITEMS.CATEGORIES.ADD);
  const canEditCategory     = hasPermission(P.INVENTORY.ITEMS.CATEGORIES.EDIT);
  const canToggleCategory   = hasPermission(P.INVENTORY.ITEMS.CATEGORIES.TOGGLE);
  const canDeleteCategory   = hasPermission(P.INVENTORY.ITEMS.CATEGORIES.DELETE);

  const [localCategories, setLocalCategories] = useState([]);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [categoryToDelete, setCategoryToDelete] = useState(null);
  const [message, setMessage] = useState("");

  const generateTempId = () => `temp-${Date.now()}-${Math.random()}`;

  // Load categories
  useEffect(() => {
    if (Array.isArray(categories)) {
      setLocalCategories(
        categories.map((c) => ({
          ...c,
          tempId: c.tempId ?? generateTempId(),
          isNew: !c.id,
          isEdited: false,
        }))
      );
    } else {
      setLocalCategories([]);
    }
  }, [categories]);

  // Add
  const handleAddCategory = () => {
    if (!canAddCategory) {
      toast.error("You don't have permission to add categories.");
      return;
    }

    const newCategory = {
      id: null,
      tempId: generateTempId(),
      name: "",
      description: "",
      displayOrder: localCategories.length + 1,
      isActive: true,
      isNew: true,
      isEdited: false,
    };
    setLocalCategories((prev) => [...prev, newCategory]);
  };

  // Update fields — guard with edit permission (allow edit on new rows since that requires add permission)
  const handleUpdateCategory = (identifier, field, value) => {
    setLocalCategories((prev) =>
      prev.map((c) => {
        if (c.id !== identifier && c.tempId !== identifier) return c;
        // New rows: require canAddCategory; existing rows: require canEditCategory
        if (c.isNew && !canAddCategory) return c;
        if (!c.isNew && !canEditCategory) return c;
        return { ...c, [field]: value, isEdited: true };
      })
    );
  };

  // Toggle active — requires TOGGLE permission (or canAddCategory for unsaved new rows)
  const toggleActive = (identifier) => {
    setLocalCategories((prev) =>
      prev.map((c) => {
        if (c.id !== identifier && c.tempId !== identifier) return c;
        if (c.isNew && !canAddCategory) return c;
        if (!c.isNew && !canToggleCategory) return c;
        return { ...c, isActive: !c.isActive, isEdited: true };
      })
    );
  };

  // Delete → open confirmation dialog
  const confirmDelete = (identifier, dbId, isNew) => {
    // New (unsaved) rows only need canAddCategory to be removed
    if (isNew && !canAddCategory) {
      toast.error("You don't have permission to remove this.");
      return;
    }
    // Existing rows need canDeleteCategory
    if (!isNew && !canDeleteCategory) {
      toast.error("You don't have permission to delete categories.");
      return;
    }
    setCategoryToDelete({ identifier, dbId, isNew });
    setShowDeleteDialog(true);
  };

  // Delete final confirmation
  const handleConfirmDelete = async () => {
    if (!categoryToDelete) return;

    const { identifier, dbId, isNew } = categoryToDelete;

    try {
      // Only call API if the category exists in the DB
      if (!isNew && dbId) {
        await inventoryitemService.deleteCategory(dbId);
      }

      setLocalCategories((prev) =>
        prev.filter((c) => c.id !== identifier && c.tempId !== identifier)
      );

      setMessage("Category deleted successfully!");
    } catch (error) {
      console.error("Delete category failed:", error);
      setMessage("Failed to delete category.");
    }

    setShowDeleteDialog(false);
    setCategoryToDelete(null);
  };

  // Cancel delete
  const handleCancelDelete = () => {
    setShowDeleteDialog(false);
    setCategoryToDelete(null);
  };

  // Save only changed or new
  const handleSave = async () => {
    try {
      const validCategories = localCategories.filter(
        (c) => c.name.trim() !== ""
      );

      for (const cat of validCategories) {
        const payload = {
          name: cat.name,
          description: cat.description,
          displayOrder: cat.displayOrder,
          isActive: cat.isActive,
          isDeleted: false,
        };

        if (cat.isNew) {
          if (!canAddCategory) continue;
          await inventoryitemService.createCategory(payload);
        } else if (cat.isEdited) {
          if (!canEditCategory) continue;
          await inventoryitemService.updateCategory(cat.id, payload);
        }
      }

      onSave(validCategories);
      setMessage("Categories saved successfully!");
      setTimeout(() => onClose(), 700);
    } catch (error) {
      console.error("Save failed:", error);
      setMessage("Failed to save categories.");
    }
  };

  // Determine if a row's delete button should be visible
  const showDeleteButton = (cat) => {
    if (cat.isNew) return canAddCategory;   // can remove an unsaved row if you can add
    return canDeleteCategory;               // can delete a saved row if you have delete perm
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-hidden relative">

        {/* HEADER */}
        <div className="bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">
              Manage Inventory Categories
            </h2>
            <p className="text-xs text-gray-600 mt-0.5">
              Organize your inventory items into categories
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 hover:bg-gray-100 rounded-md transition-colors"
          >
            <X size={20} className="text-gray-500" />
          </button>
        </div>

        {/* CATEGORY LIST */}
        <div className="overflow-y-auto max-h-[calc(90vh-180px)] p-6">
          <div className="space-y-3">
            {localCategories.map((cat, index) => {
              const identifier = cat.id || cat.tempId;
              // Existing rows: fields editable with canEditCategory, checkbox with canToggleCategory
              // New rows: both controlled by canAddCategory
              const isFieldReadOnly    = cat.isNew ? !canAddCategory : !canEditCategory;
              const isToggleReadOnly   = cat.isNew ? !canAddCategory : !canToggleCategory;

              return (
                <div
                  key={identifier}
                  className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg border border-gray-200"
                >
                  <div className="flex items-center justify-center w-8 h-8 bg-teal-100 text-teal-700 rounded font-semibold text-sm">
                    {index + 1}
                  </div>

                  <input
                    type="text"
                    value={cat.name}
                    onChange={(e) =>
                      handleUpdateCategory(identifier, "name", e.target.value)
                    }
                    placeholder="Category name"
                    disabled={isFieldReadOnly}
                    className={`flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-1 focus:ring-teal-500 focus:border-teal-500 ${
                      isFieldReadOnly ? "bg-gray-100 cursor-not-allowed text-gray-500" : ""
                    }`}
                  />

                  <input
                    type="text"
                    value={cat.description}
                    onChange={(e) =>
                      handleUpdateCategory(identifier, "description", e.target.value)
                    }
                    placeholder="Description (optional)"
                    disabled={isFieldReadOnly}
                    className={`flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-1 focus:ring-teal-500 focus:border-teal-500 ${
                      isFieldReadOnly ? "bg-gray-100 cursor-not-allowed text-gray-500" : ""
                    }`}
                  />

                  <label
                    className={`flex items-center gap-2 ${isToggleReadOnly ? "cursor-not-allowed opacity-60" : "cursor-pointer"}`}
                  >
                    <input
                      type="checkbox"
                      checked={cat.isActive}
                      onChange={() => !isToggleReadOnly && toggleActive(identifier)}
                      disabled={isToggleReadOnly}
                      className="w-4 h-4 rounded border-gray-300 text-teal-600"
                    />
                    <span className="text-xs text-gray-600">Active</span>
                  </label>

                  {showDeleteButton(cat) && (
                    <button
                      onClick={() => confirmDelete(identifier, cat.id, cat.isNew)}
                      className="p-2 text-red-600 hover:bg-red-50 rounded-md"
                    >
                      <Trash2 size={16} />
                    </button>
                  )}
                </div>
              );
            })}

            {/* Only render Add button if user has the permission */}
            {canAddCategory && (
              <button
                onClick={handleAddCategory}
                className="w-full py-3 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-teal-500 hover:text-teal-600 transition-colors flex items-center justify-center gap-2 text-sm font-medium"
              >
                <Plus size={18} />
                <span>Add Category</span>
              </button>
            )}
          </div>
        </div>

        {/* FOOTER */}
        <div className="border-t border-gray-200 px-6 py-3 bg-gray-50 flex items-center justify-between">
          <p className="text-xs text-gray-600">
            {localCategories.length} categor{localCategories.length !== 1 ? "ies" : "y"}
          </p>

          <div className="flex gap-2">
            <button
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-100 text-sm"
            >
              Cancel
            </button>
            {(canAddCategory || canEditCategory || canToggleCategory) && (
              <button
                onClick={handleSave}
                className="px-4 py-2 bg-teal-600 text-white rounded-md hover:bg-teal-700 text-sm"
              >
                Save Categories
              </button>
            )}
          </div>
        </div>

        {/* MESSAGE */}
        {message && (
          <div className="px-6 py-2 text-sm text-gray-700 text-center">
            {message}
          </div>
        )}

        {/* DELETE CONFIRM POPUP */}
        {showDeleteDialog && (
          <div className="absolute inset-0 bg-black bg-opacity-40 flex items-center justify-center">
            <div className="bg-white rounded-lg p-6 shadow-lg w-80 text-center">
              <AlertTriangle size={40} className="text-red-500 mx-auto mb-2" />
              <h3 className="text-lg font-semibold text-gray-800 mb-2">
                Delete Category?
              </h3>
              <p className="text-sm text-gray-600 mb-4">
                Are you sure you want to delete this category? This action cannot be undone.
              </p>
              <div className="flex justify-center gap-3">
                <button
                  onClick={handleCancelDelete}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-100 text-sm"
                >
                  Cancel
                </button>
                <button
                  onClick={handleConfirmDelete}
                  className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 text-sm"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        )}

      </div>
    </div>
  );
}